/**
 * @file StageListEditor.tsx
 * @description Настройка стадий воронки — общий редактор для «Стадий
 * заказов» и «Стадий возвратов» (phase10.5 шаги 3 и 8; phase19.31 —
 * компактный список по разбору A).
 *
 * Заголовок — в верхней панели («Стадии заказов», без «PIPELINE» и номеров
 * фаз), главное действие «+ Стадия». Список: ⠿ · ● название · «N заказов ·
 * срок 24 ч»; правка — по нажатию на строку, удаление — в «⋯» с
 * подтверждением. Порядок меняется перетаскиванием: сразу на экране, затем
 * на сервере; отказ — откат и понятный текст.
 */

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ListOrdered, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { StageDeleteDialog } from "./StageDeleteDialog";
import type { StageDialogState, StageListConfig, StageListItem } from "./stage-list-types";
import { StageListRow } from "./StageListRow";
import { Button } from "../../ui/button";
import { CabinetPageHeader } from "../../ui/CabinetPageHeader";
import { EmptyState } from "../../ui/EmptyState";
import { LIST_FRAME_CLASS, ListError, ListSkeleton } from "../../ui/ListStates";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { cn } from "@/lib/utils";

interface Props<T extends StageListItem> {
  config: StageListConfig<T>;
  pipelineId: string | undefined;
  /** Право менять стадии: без него список только для чтения. */
  canConfig: boolean;
}

export function StageListEditor<T extends StageListItem>({ config, pipelineId, canConfig }: Props<T>) {
  const notify = useNotify();
  const [stages, setStages] = useState<T[]>([]);
  const [pipelineName, setPipelineName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<StageDialogState<T> | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const { load } = config;

  const refresh = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const res = await load(pipelineId);
      setStages(res.stages);
      setPipelineName(res.pipelineName);
      setError(res.pipelineName === null ? "Воронка не найдена" : null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить стадии");
    } finally {
      setLoading(false);
    }
  }, [load, pipelineId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id || !pipelineId) return;
    const from = stages.findIndex((s) => s.id === active.id);
    const to = stages.findIndex((s) => s.id === over.id);
    if (from < 0 || to < 0) return;
    const before = stages;
    const next = arrayMove(stages, from, to);
    setStages(next);
    try {
      setStages(await config.reorder(pipelineId, next.map((s, i) => ({ id: s.id, sortOrder: (i + 1) * 10 }))));
    } catch (err) {
      setStages(before);
      notify.error(err instanceof ApiError ? err.message : "Не удалось поменять порядок стадий");
    }
  }

  async function confirmDelete() {
    if (!deleting || !pipelineId) return;
    setBusy(true);
    try {
      await config.remove(pipelineId, deleting.id);
      notify.success("Стадия удалена");
      setDeleting(null);
      void refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить стадию");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={config.title}
        subtitle={pipelineName ? `Воронка «${pipelineName}» · порядок меняется перетаскиванием` : undefined}
        action={
          canConfig && pipelineId ? (
            <Button variant="brand" onClick={() => setDialog({ mode: "create" })}>
              <Plus className="size-4" />
              Стадия
            </Button>
          ) : undefined
        }
      />

      {error ? <ListError message={error} onRetry={() => void refresh()} /> : null}

      {loading ? (
        <ListSkeleton rows={6} compact />
      ) : stages.length === 0 ? (
        error ? null : <EmptyState icon={ListOrdered} title="Стадий пока нет" description="Добавьте первую стадию воронки." />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void onDragEnd(e)}>
          <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <ul aria-label={config.title} className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
              {stages.map((stage) => (
                <StageListRow
                  key={stage.id}
                  stage={stage}
                  count={config.countOf(stage)}
                  countWords={config.countWords}
                  canConfig={canConfig}
                  onEdit={() => setDialog({ mode: "edit", stage })}
                  onDelete={() => setDeleting(stage)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {dialog && pipelineId
        ? config.renderDialog({
            pipelineId,
            state: dialog,
            onClose: () => setDialog(null),
            onSaved: () => {
              setDialog(null);
              void refresh();
            },
          })
        : null}

      <StageDeleteDialog
        name={deleting?.name ?? null}
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
